import React from 'react';
import { Box, LinearProgress, Tooltip, Typography } from '@mui/material';
import { keyframes } from '@mui/material/styles';
import StorageIcon from '@mui/icons-material/Storage';
import { StorageUsage } from '../AssetExplorer.types';

/** Hardcoded so the critical glow stays red in light, dark, and professional-light themes. */
const STORAGE_CRITICAL_RED = '#ff1744';
const STORAGE_CRITICAL_THRESHOLD = 90;

const redGlowPulse = keyframes`
    0%, 100% {
        box-shadow: 0 0 4px 1px ${STORAGE_CRITICAL_RED}, 0 0 10px 3px rgba(255, 23, 68, 0.55);
    }
    50% {
        box-shadow: 0 0 8px 2px ${STORAGE_CRITICAL_RED}, 0 0 18px 6px rgba(255, 23, 68, 0.9);
    }
`;

const formatGb = (gb: number): string => {
    if (!Number.isFinite(gb) || gb <= 0) return '0';
    if (gb < 0.001) return '< 0.001';
    if (gb < 1) return gb.toFixed(3);
    if (gb < 10) return gb.toFixed(2);
    if (gb < 100) return gb.toFixed(1);
    return String(Math.round(gb));
};

export interface AssetStorageUsageBarProps {
    usage: StorageUsage | null;
}

/** Compact used-vs-plan GB bar for the Asset Explorer pagination footer. */
export const AssetStorageUsageBar: React.FC<AssetStorageUsageBarProps> = ({ usage }) => {
    if (!usage) return null;

    const hasPlan = usage.totalSizeInPlan != null && usage.totalSizeInPlan > 0;
    const percent = hasPlan ? Math.max(0, usage.percentUsed ?? 0) : 0;
    const isCritical = hasPlan && percent > STORAGE_CRITICAL_THRESHOLD;
    const progressValue = hasPlan ? Math.min(100, percent) : 0;
    const planGb = usage.totalSizeInPlan ?? 0;

    const label = hasPlan
        ? `${formatGb(usage.usedGb)} / ${formatGb(planGb)} GB`
        : `${formatGb(usage.usedGb)} GB used`;

    const tooltip = hasPlan
        ? `Cloud storage: ${formatGb(usage.usedGb)} GB of ${formatGb(planGb)} GB (${percent.toFixed(1)}%)`
        : `Cloud storage used: ${formatGb(usage.usedGb)} GB (no plan quota)`;

    return (
        <Box sx={{ flex: '1 1 auto', minWidth: 0, width: '100%', display: 'flex' }}>
            <Tooltip title={tooltip} arrow>
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 0.75,
                        flex: '1 1 auto',
                        minWidth: 0,
                        width: '100%',
                        maxWidth: '100%',
                        height: 32,
                        px: 0.75,
                        py: 0.25,
                        borderRadius: 1,
                        ...(isCritical && {
                            animation: `${redGlowPulse} 1.4s ease-in-out infinite`,
                            backgroundColor: 'rgba(255, 23, 68, 0.12)',
                        }),
                    }}
                >
                    <StorageIcon
                        sx={{
                            fontSize: 16,
                            color: isCritical ? STORAGE_CRITICAL_RED : 'text.secondary',
                            flexShrink: 0,
                        }}
                    />
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography
                            variant="caption"
                            noWrap
                            sx={{
                                display: 'block',
                                fontSize: '0.75rem',
                                lineHeight: 1.2,
                                color: isCritical ? STORAGE_CRITICAL_RED : 'text.secondary',
                                fontWeight: isCritical ? 700 : 500,
                            }}
                        >
                            {label}
                        </Typography>
                        <LinearProgress
                            variant="determinate"
                            value={progressValue}
                            sx={{
                                mt: 0.25,
                                height: 6,
                                borderRadius: 1,
                                backgroundColor: isCritical ? 'rgba(255, 23, 68, 0.25)' : 'action.hover',
                                '& .MuiLinearProgress-bar': {
                                    backgroundColor: isCritical ? STORAGE_CRITICAL_RED : 'secondary.main',
                                    borderRadius: 1,
                                },
                            }}
                        />
                    </Box>
                </Box>
            </Tooltip>
        </Box>
    );
};

export default AssetStorageUsageBar;
